
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';

const ContactPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact NeuraSites AI",
    "description": "Get in touch with NeuraSites AI for your web development project",
    "url": `${siteUrl}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "NeuraSites AI",
      "telephone": "+1-208-809-3100",
      "email": "hello@neurasites.ai",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2018 N 8th St, Unit B",
        "addressLocality": "Boise",
        "addressRegion": "ID",
        "postalCode": "83702",
        "addressCountry": "US"
      }
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('/api/vibe-check.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'contact-form',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          businessName: formData.company || 'Not provided',
          notes: formData.message,
          goals: formData.message || 'Contact form inquiry',
          audience: 'Not provided from contact form',
          mustHave: 'Not provided from contact form'
        })
      });
      const responseData = await response.json().catch(() => null);

      if (response.ok && responseData?.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title="Contact Us - Get Your AI-Powered Website | NeuraSites AI"
        description="Ready to transform your business with an AI-powered website? Contact NeuraSites AI today. Call (208) 809-3100 or send us a message to get started."
        keywords="contact web developer, AI website consultation, web development quote, Boise web design, website project inquiry"
        ogTitle="Contact NeuraSites AI - Start Your Website Project Today"
        ogDescription="Ready to transform your business with an AI-powered website? Get in touch and let's discuss your project."
        canonicalUrl={`${siteUrl}/contact`}
        structuredData={structuredData}
      />
      
      <Header />
      
      <main className="">
        {/* Hero Section */}
        <section className="relative overflow-hidden brand-soft-surface py-20">
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('/assets/pricing_hero-CCxYm27z.jpg')` }}
            />
            <div className="absolute inset-0 brand-soft-overlay" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Let's Build Something
              <span className="block brand-heading-accent">
                Amazing Together
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              Ready to transform your business with an AI-powered website? Get in touch and let's discuss your project.
            </p>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              
              {/* Contact Form */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Send Us a Message</h2>
                
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                      <i className="ri-check-circle-fill text-green-500 text-xl mr-3"></i>
                      <div>
                        <h3 className="font-semibold text-green-800">Message Sent Successfully!</h3>
                        <p className="text-green-700">We'll get back to you within 24 hours.</p>
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                      <i className="ri-error-warning-fill text-red-500 text-xl mr-3"></i>
                      <div>
                        <h3 className="font-semibold text-red-800">Submission Error</h3>
                        <p className="text-red-700">Please try again or contact us directly.</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your company"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Send us a message about your project, goals, or questions..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all whitespace-nowrap ${
                      isSubmitting
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-send-plane-fill ml-2"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                {/* Contact Details */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Get in Touch</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4 rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                      <div className="w-12 h-12 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-fill text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Phone</h4>
                        <p className="text-gray-600 dark:text-gray-300">Call us for immediate assistance</p>
                        <a href="tel:208-809-3100" className="text-blue-600 font-medium hover:text-blue-700">
                          (208) 809-3100
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                      <div className="w-12 h-12 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-fill text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Email</h4>
                        <p className="text-gray-600 dark:text-gray-300">Send us an email anytime</p>
                        <a href="mailto:hello@neurasites.ai" className="text-blue-600 font-medium hover:text-blue-700">
                          hello@neurasites.ai
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                      <div className="w-12 h-12 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-fill text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Address</h4>
                        <p className="text-gray-600 dark:text-gray-300">By appointment only</p>
                        <p className="text-gray-700 dark:text-gray-200">
                          2018 N 8th St, Unit B<br />
                          Boise, ID 83702
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                      <div className="w-12 h-12 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-fill text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Business Hours</h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          Monday - Friday: 9:00 AM - 5:00 PM<br />
                          Saturday &amp; Sunday: Closed
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Quick Actions</h3>
                  
                  <div className="space-y-4">
                    <a
                      href="/vibe-check"
                      className="group flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50 p-4 transition-colors hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-950/30 dark:hover:bg-blue-950/50"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="ri-rocket-fill text-blue-600 text-xl"></i>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">Start New Project</h4>
                          <p className="text-gray-600 dark:text-gray-300 text-sm">Complete our detailed intake form</p>
                        </div>
                      </div>
                      <i className="ri-arrow-right-line text-blue-600 group-hover:translate-x-1 transition-transform"></i>
                    </a>

                    <a
                      href="/portfolio"
                      className="group flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:bg-gray-900/70"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="ri-gallery-fill text-gray-600 dark:text-gray-300 text-xl"></i>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">View Our Work</h4>
                          <p className="text-gray-600 dark:text-gray-300 text-sm">See our latest projects and case studies</p>
                        </div>
                      </div>
                      <i className="ri-arrow-right-line text-gray-600 dark:text-gray-300 group-hover:translate-x-1 transition-transform"></i>
                    </a>

                    <a
                      href="/services"
                      className="group flex items-center justify-between rounded-lg border border-green-100 bg-green-50 p-4 transition-colors hover:bg-green-100 dark:border-green-900/40 dark:bg-green-950/20 dark:hover:bg-green-950/35"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="ri-service-fill text-green-600 text-xl"></i>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">Our Services</h4>
                          <p className="text-gray-600 dark:text-gray-300 text-sm">Explore our full range of offerings</p>
                        </div>
                      </div>
                      <i className="ri-arrow-right-line text-green-600 group-hover:translate-x-1 transition-transform"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Quick answers to common questions about our services
              </p>
            </div>

            <div className="space-y-4">
              {[
                { question: "How long does it take to build a website?", answer: "Most websites are completed within 2-4 weeks, depending on complexity. Simple brochure sites can be done in 1-2 weeks, while complex e-commerce or SaaS platforms may take 4-6 weeks." },
                { question: "What's included in the $999 starting price?", answer: "Our $999 package includes a 5-page responsive website, basic SEO optimization, contact forms, mobile optimization, and 30 days of support. Additional pages and features are available as add-ons." },
                { question: "Do you provide ongoing maintenance?", answer: "Yes. We offer monthly maintenance plans that include security updates, backups, performance monitoring, and content updates." },
                { question: "Can you help with content writing?", answer: "Absolutely. Our AI-assisted copy process creates clear, conversion-focused content aligned with your brand voice." },
                { question: "Do you work with existing websites?", answer: "Yes, we can redesign your current website or improve specific pages while preserving what already works." },
                { question: "What if I need changes after launch?", answer: "All projects include post-launch revisions, and we offer ongoing support plans for regular updates." }
              ].map((faq, index) => (
                <details key={index} className="group bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-semibold text-gray-900 dark:text-white">
                    <span>{faq.question}</span>
                    <i className="ri-add-line text-xl group-open:hidden"></i>
                    <i className="ri-subtract-line text-xl hidden group-open:block"></i>
                  </summary>
                  <p className="text-gray-600 dark:text-gray-300 mt-4">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
